/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { ComboBox } from "@eds/react";
import type { Option } from "@eds/react";
import { useCallback } from "react";
import type { FilterOption } from "../core/Directory.types";

export type DirectoryComboBoxProps = {
	options: FilterOption[];
	values: string[];
	onChange: (values: string[]) => void;
	/**
	 * Accessible label for the combobox. Required so the rendered control
	 * always has an a11y name (WCAG 4.1.2). Use the FilterDef's `label`.
	 */
	label: string;
	placeholder?: string;
	/**
	 * aria-label for the "remove this selected option" button on each chip inside the
	 * ComboBox. EDS requires this when `multiple=true`. Defaults to "Remove".
	 */
	removeLabel?: string;
};

/**
 * Multi-value searchable combobox. Selected values surface as chips inside the EDS
 * ComboBox component and can be removed individually. Engine-level semantics (OR/AND)
 * are controlled by the FilterDef's `matchMode`.
 */
export function DirectoryComboBox({
	options,
	values,
	onChange,
	label,
	placeholder,
	removeLabel = "Remove",
}: DirectoryComboBoxProps): React.JSX.Element {
	if (process.env.NODE_ENV !== "production" && !label.trim()) {
		console.warn(
			"[DirectoryComboBox] An empty `label` was passed; the rendered combobox will have no accessible name. Supply a meaningful label or set a localized fallback at the FilterDef.",
		);
	}

	const edsOptions: Option<string>[] = options.map((o) => ({
		label: o.label,
		value: o.value,
		disabled: o.disabled,
	}));

	const selected = edsOptions.filter((o) => values.includes(o.value));

	const handleChange = useCallback(
		(next: Option<string>[]) => {
			onChange(next.map((o) => o.value));
		},
		[onChange],
	);

	return (
		<ComboBox<string, true>
			label={label}
			placeholder={placeholder}
			options={edsOptions}
			multiple
			value={selected}
			onChange={handleChange}
			removeLabel={removeLabel}
		/>
	);
}
